<script lang="ts">
    import AttributeAnswers from "@apps/reviews/components/AttributeAnswers/AttributeAnswers.svelte";
    import ReviewCheckCircleIcon from "@apps/reviews/components/icons/ReviewCheckCircleIcon.svelte";
    import ReviewRatingStars from "@apps/reviews/components/RatingStars/ReviewRatingStars.svelte";
    import ReviewImageList from "@apps/reviews/components/ReviewImageList/ReviewImageList.svelte";
    import ReviewsTranslateButton from "@apps/reviews/components/ReviewsTranslateButton.svelte";
    import ToggleVote from "@apps/reviews/components/ToggleVote.svelte";
    import { REVIEWS_REPLY_STATUS } from "@apps/reviews/constants";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { VotesState } from "@apps/reviews/stores/VotesStore";
    import { getHumanReadableDate } from "@lib/utils";

    import Badge from "@components/Badge/Badge.svelte";
    import Icon from "@components/Icon.svelte";
    import ClockIcon from "@components/icons/ClockIcon.svelte";
    import InfoIcon from "@components/icons/InfoIcon.svelte";
    import T from "@components/T.svelte";
    import TooltipActivator from "@components/Tooltip/TooltipActivator.svelte";
    import ViewMore from "@components/ViewMore.svelte";

    import ReviewsProduct from "../../ReviewsProduct.svelte";
    import ReviewsUnavailableProduct from "../../ReviewsUnavailableProduct.svelte";
    import ReviewReply from "../ReviewReply/ReviewReply.svelte";
    import ReviewItemFrame from "./ReviewItemFrame.svelte";

    export let namespace: string;
    export let review: ReviewEntity;
    export let customerName: string;
    export let translationEnabled: boolean;
    export let translsatedContent: {
        title: string | null;
        body: string;
        reply: string | null;
    };
    export let showTranslation: boolean;
    export let isTranslationLoading: boolean;
    export let showTitle: boolean;
    export let showProducts: boolean;
    export let votesEnabled: boolean;
    export let reviewVotes: VotesState | undefined;
    export let isIncentivizedEnabled: boolean;
    export let currentProductId: number | null | undefined;
</script>

<ReviewItemFrame>
    <svelte:fragment slot="author">
        <span class="gw-rv-item__author">
            {customerName}
        </span>
    </svelte:fragment>

    <svelte:fragment slot="verified-buyer">
        {#if review.customer?.isVerifiedBuyer}
            <Badge
                variant="regular"
                icon={ReviewCheckCircleIcon}
                size="md"
                transparent
                alignCenter
            >
                <T {namespace} translationKey="verified_buyer" />
            </Badge>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="see-translation-btn">
        {#if translationEnabled}
            <ReviewsTranslateButton
                {namespace}
                {review}
                {showTranslation}
                {isTranslationLoading}
                on:seeTranslation
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="published">
        {#if !review.isPublished}
            <Badge icon={ClockIcon} size="lg" variant="flat" transparent>
                <T {namespace} translationKey="under_review" />
            </Badge>
        {/if}
    </svelte:fragment>

    <ReviewRatingStars
        slot="rating"
        rating={review.rating}
        width={"1.4em"}
        height={"1.4em"}
    />

    <svelte:fragment slot="date">
        <div class="gw-rv-item__date">
            {getHumanReadableDate(review.createdAt)}
        </div>
    </svelte:fragment>

    <svelte:fragment slot="title">
        {#if (showTitle && review.title) || (review.isIncentivized && isIncentivizedEnabled)}
            <div class="gw-rv-item__title-bar">
                {#if showTitle && review.title}
                    <span class="gw-rv-item__title">
                        {translsatedContent.title || ""}
                    </span>
                {/if}

                {#if isIncentivizedEnabled && review.isIncentivized}
                    <div class="gw-rv-item__incentivized-wrapper">
                        <TooltipActivator position="top-end" maxWidth="308px">
                            <div class="gw-rv-item__incentivized">
                                <T {namespace} translationKey="incentivized" />

                                <Icon
                                    width="14"
                                    height="14"
                                    color="#12b76a"
                                    stroke="#12b76a"
                                >
                                    <InfoIcon />
                                </Icon>
                            </div>

                            <T
                                slot="content"
                                {namespace}
                                translationKey="incentivized_content"
                            />
                        </TooltipActivator>
                    </div>
                {/if}
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        <ViewMore text={translsatedContent.body || ""} class="gw-rv-item__text">
            <svelte:fragment slot="view_more_text">
                <T {namespace} translationKey="view_more" />
            </svelte:fragment>
            <svelte:fragment slot="view_less_text">
                <T {namespace} translationKey="view_less" />
            </svelte:fragment>
        </ViewMore>
    </svelte:fragment>

    <svelte:fragment slot="customer-attributes">
        {#if review.customerAttributeAnswers.length > 0}
            <div class="gw-rv-item__attributes">
                <AttributeAnswers
                    attributeAnswers={review.customerAttributeAnswers}
                />
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="images">
        {#if review.images}
            <ReviewImageList
                on:openReview
                images={review.images}
                reviewTitle={translsatedContent.title || ""}
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-attributes">
        {#if review.productAttributeAnswers.length > 0}
            <div class="gw-rv-item__attributes">
                <AttributeAnswers
                    attributeAnswers={review.productAttributeAnswers}
                />
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="vote">
        {#if votesEnabled && reviewVotes}
            <ToggleVote {reviewVotes} on:toggleVote />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="reply">
        {#if review.reply && review.reply.status === REVIEWS_REPLY_STATUS.PUBLIC}
            <ReviewReply {namespace} reply={translsatedContent.reply || ""} />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="footer">
        {#if showProducts && review.product?.id}
            {#if review.product?.isDraft || review.product?.isDeleted}
                <div class="gw-rv-item__footer-wrapper">
                    <ReviewsUnavailableProduct
                        productId={review.product.id}
                        token={review.product?.unavailableProductToken || ""}
                        on:productReady
                    />
                </div>
            {:else if !currentProductId || review.product.id !== currentProductId}
                <div class="gw-rv-item__footer-wrapper">
                    <ReviewsProduct
                        productId={review.product.id}
                        on:productReady
                    />
                </div>
            {/if}
        {/if}
    </svelte:fragment>
</ReviewItemFrame>

<style lang="postcss">
    .gw-rv-item {
        &__author {
            font-size: 1em;
            font-weight: 600;
        }

        &__title-bar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: nowrap;
            gap: 0.75rem;
        }

        &__title {
            width: 100%;
            display: inline-block;
            font-weight: 600;
            font-size: 0.875em;
        }

        &__incentivized-wrapper {
            display: flex;
            justify-content: flex-end;
            flex: 1;
        }

        &__incentivized {
            display: flex;
            align-items: center;
            gap: 0.25em;
            font-size: 0.875em;
            font-style: normal;
            font-weight: 400;
            line-height: 1.4;
            cursor: pointer;
            opacity: 0.7;
        }

        :global(&__text) {
            font-size: 0.875em;

            word-break: break-word;
            overflow-wrap: break-word;
            margin: 0;
        }

        &__attributes {
            margin: 1em 0 0;
        }

        &__date {
            opacity: 0.7;
            font-size: 0.75em;
            line-height: 1em;
        }

        &__footer-wrapper {
            margin-top: 0.75em;
        }
    }
</style>
